import * as React from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import moment, { Moment } from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import { useSession } from '@hooks';
import { useListMyBookingsQuery, useListMyEventsQuery } from '@graphql';
import { refetchEventsVar, refetchBookingsVar } from '@context';
import { shouldDisableBookingDate, CALENDAR_LAST_KNOWN_DATE_KEY } from '@utils';
import BookingSlot from './BookingSlot';
import EventSlot from './EventSlot';

interface MobileCalendarProps {
  accountId: string;
  variant: 'all' | 'booking' | 'event';
}

const RenderDay = (props: PickersDayProps<Moment> & { highlightedDays?: number[] }) => {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  const isSelected = !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;

  return (
    <Badge
      key={props.day}
      color="info"
      overlap="circular"
      badgeContent={isSelected ? ' ' : undefined}
      variant={isSelected ? 'dot' : undefined}
    >
      <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
    </Badge>
  );
};

export function MobileCalendar(props: Readonly<MobileCalendarProps>) {
  const { variant } = props;
  const theme = useTheme();
  const { account } = useSession();
  const [value, setValue] = React.useState<Moment>(moment());
  const [visibleMonth, setVisibleMonth] = React.useState<Moment>(moment().startOf('month'));
  const hasInitializedFromStorage = React.useRef(false);
  const dateRange = React.useMemo(
    () => ({
      from: visibleMonth.clone().startOf('month').toISOString(),
      to: visibleMonth.clone().endOf('month').toISOString(),
    }),
    [visibleMonth]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isRefetchEvents = useReactiveVar(refetchEventsVar);
  const isRefetchBookings = useReactiveVar(refetchBookingsVar);
  const { t } = useTranslation('common');

  const bookingQuery = useListMyBookingsQuery({
    variables: { dateRange },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const eventQuery = useListMyEventsQuery({
    variables: { dateRange },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const bookings = bookingQuery.data?.myBookings ?? [];
  const events = eventQuery.data?.myEvents ?? [];

  const highlightedDays = React.useMemo(() => {
    const bookingDays = bookings.map((booking) => moment(booking.startTime).get('date'));
    const eventDays = events.map((event) => moment(event.from).get('date'));

    if (variant === 'booking') return [...new Set(bookingDays)];
    if (variant === 'event') return [...new Set(eventDays)];
    return [...new Set(eventDays.concat(bookingDays))];
  }, [variant, bookings, events]);

  const toDisplay = React.useMemo(() => {
    const matchingBookings = bookings.filter((b) => moment(b.startTime).isSame(value, 'day'));
    const matchingEvents = events.filter((e) => moment(e.from).isSame(value, 'day'));

    if (variant === 'booking') return matchingBookings;
    if (variant === 'event') return matchingEvents;
    return [...matchingBookings, ...matchingEvents];
  }, [variant, value, bookings, events]);

  const handleMonthChange = (date: Moment) => {
    setVisibleMonth(date.clone().startOf('month'));
  };

  const handleDateClick = (date: Moment | null) => {
    if (!date) return;

    setValue(date);
    setVisibleMonth(date.clone().startOf('month'));
  };

  React.useEffect(() => {
    if (hasInitializedFromStorage.current) return;
    if (location.pathname !== '/me/calendar/event') return;

    const hasDialogParams = ['date', 's', 'e', 'c'].some((key) => searchParams.has(key));
    if (hasDialogParams) {
      hasInitializedFromStorage.current = true;
      return;
    }

    const date = localStorage.getItem(CALENDAR_LAST_KNOWN_DATE_KEY);

    if (date && moment(date, 'YYYY-MM-DD', true).isValid()) {
      const nextValue = moment(date);
      setValue(nextValue);
      setVisibleMonth(nextValue.clone().startOf('month'));
    }

    hasInitializedFromStorage.current = true;
  }, [location.pathname, searchParams]);

  React.useEffect(() => {
    const newDate = value.format('YYYY-MM-DD');
    if (newDate === searchParams.get('date')) return;
    localStorage.setItem(CALENDAR_LAST_KNOWN_DATE_KEY, newDate);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('date', newDate);
    setSearchParams(nextSearchParams, { replace: true });
  }, [value, searchParams, setSearchParams]);

  React.useEffect(() => {
    // Refetch events and bookings when necessary (new or deleted items)
    if (isRefetchEvents) {
      eventQuery.refetch({ dateRange }).finally(() => {
        refetchEventsVar(false);
      });
    }
    if (isRefetchBookings) {
      bookingQuery.refetch({ dateRange }).finally(() => {
        refetchBookingsVar(false);
      });
    }
  }, [isRefetchEvents, isRefetchBookings, dateRange, eventQuery.refetch, bookingQuery.refetch]);

  return (
    <Stack spacing={0} height="100%" sx={{ overflowY: 'hidden' }}>
      <Stack>
        <DateCalendar
          loading={bookingQuery.loading || eventQuery.loading}
          renderLoading={() => <CircularProgress />}
          showDaysOutsideCurrentMonth
          displayWeekNumber
          value={value}
          onMonthChange={handleMonthChange}
          onChange={handleDateClick}
          shouldDisableDate={(date) => {
            if (account?.__typename === 'Spot') {
              const { opening, closing } = account;
              return shouldDisableBookingDate(date, opening, closing);
            }
            return false;
          }}
          sx={{ width: '100%', height: '-webkit-fill-available' }}
          slots={{
            day: RenderDay,
          }}
          slotProps={{
            day: {
              highlightedDays,
            } as any,
          }}
        />
      </Stack>
      <Divider sx={{ backgroundColor: theme.palette.grey[600] }} />
      <Stack py={1} flex={1} sx={{ overflowY: 'auto' }}>
        <Stack minHeight="calc(100% + 1px)">
          {toDisplay.length ? (
            <Stack px={1} spacing={1} id="selected-account-event-date">
              {toDisplay.map((data) => {
                if (data.__typename === 'Booking') {
                  return <BookingSlot key={data.id} booking={data} />;
                } else if (data.__typename === 'Event') {
                  return <EventSlot key={data.id} event={data} />;
                }
              })}
            </Stack>
          ) : (
            <Stack px={1}>
              <Stack
                p={3}
                sx={{ backgroundColor: theme.palette.background.paper, borderRadius: theme.borderRadius }}
              >
                <Typography fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                  {t('calendar.Nothing this day')}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
